import React, { useState } from 'react';
import { ArrowLeftRight } from 'lucide-react';
import { marketplaceApi } from '../../api/marketplace';
import { cardsApi } from '../../api/cards';
import CounterOfferCard from './CounterOfferCard';
import { useLanguage } from '../../i18n/LanguageContext';

const OffersTab = ({ offers, counterOffers = [], loading, user, onOfferUpdated }) => {
  const { t, locale } = useLanguage();
  const [processing, setProcessing] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showCounterOfferModal, setShowCounterOfferModal] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [userCards, setUserCards] = useState([]);
  const [loadingCards, setLoadingCards] = useState(false);
  const [selectedCounterCardId, setSelectedCounterCardId] = useState(null);

  const handleAcceptTrade = async (tradeId) => {
    if (!user?.id) return;
    setProcessing(true);
    try {
      await marketplaceApi.acceptTrade(tradeId, user.id);
      setMessage({ type: 'success', text: t('marketplace.tradeAcceptSuccess') });
      onOfferUpdated();
    } catch (error) {
      setMessage({ type: 'error', text: `❌ ${error.message || t('trade.acceptFailed')}` });
    } finally {
      setProcessing(false);
    }
  };

  const handleRejectTrade = async (tradeId) => {
    if (!user?.id) return;
    setProcessing(true);
    try {
      await marketplaceApi.rejectTrade(tradeId, user.id);
      setMessage({ type: 'success', text: t('marketplace.tradeRejectSuccess') });
      onOfferUpdated();
    } catch (error) {
      setMessage({ type: 'error', text: `❌ ${error.message || t('trade.rejectFailed')}` });
    } finally {
      setProcessing(false);
    }
  };

  const handleOpenCounterOffer = async (offer) => {
    if (!user?.id) return;
    setSelectedOffer(offer);
    setLoadingCards(true);
    try {
      const response = await cardsApi.getUserCards(user.id, {});
      if (response?.success && response?.cards) {
        // Filter out cards that are listed, in use, or the one being traded
        const availableCards = response.cards.filter(c => 
          !c.is_in_sale && 
          !c.is_in_auction && 
          !c.is_on_loan && 
          c.id !== offer.card2Id // Can't counter-offer with the card they're asking for
        );
        setUserCards(availableCards);
        if (availableCards.length === 0) {
          setMessage({ type: 'error', text: t('trade.noCardsForCounter') });
          return;
        }
        setShowCounterOfferModal(true);
      }
    } catch (error) {
      setMessage({ type: 'error', text: `❌ ${error.message || t('trade.loadCardsFailed')}` });
    } finally {
      setLoadingCards(false);
    }
  };

  const handleCreateCounterOffer = async () => {
    if (!selectedCounterCardId || !user?.id || !selectedOffer) {
      setMessage({ type: 'error', text: t('trade.selectCardForCounter') });
      return;
    }
    setProcessing(true);
    try {
      // Create new trade: user (who received offer) offers their card for the original card offered
      await marketplaceApi.createTrade({
        user1Id: user.id, // User making counter offer
        user2Id: selectedOffer.user1Id, // Original offer maker
        card1Id: selectedCounterCardId, // Card user wants to give
        card2Id: selectedOffer.card1Id // Card user wants to receive (the one originally offered)
      });
      // Reject the original trade
      await marketplaceApi.rejectTrade(selectedOffer.id, user.id);
      setMessage({ type: 'success', text: t('marketplace.counterOfferSuccess') });
      setShowCounterOfferModal(false);
      setSelectedOffer(null);
      setSelectedCounterCardId(null);
      onOfferUpdated();
    } catch (error) {
      setMessage({ type: 'error', text: `❌ ${error.message || t('trade.createCounterFailed')}` });
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div>
      {message.text && (
        <div className={`mb-4 p-4 rounded-lg ${
          message.type === 'success' ? 'bg-green-500/20 text-green-300' : 'bg-red-500/20 text-red-300'
        }`}>
          {message.text}
        </div>
      )}

      {loading ? (
        <div className="text-center py-12">
          <div className="football-spinner mx-auto"></div>
          <p className="mt-4 text-gray-400">{t('common.loading')}</p>
        </div>
      ) : offers.length === 0 && counterOffers.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <ArrowLeftRight className="w-16 h-16 mx-auto mb-4 opacity-50" />
          <p>{t('marketplace.emptyTradesBody')}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Counter Offers Section */}
          {counterOffers.length > 0 && (
            <>
              <h3 className="text-xl font-bold text-[#1e293b] mb-4">{t('trade.counterOfferTitle')}</h3>
              {counterOffers.map((counterOffer) => (
                <CounterOfferCard 
                  key={counterOffer.id} 
                  counterOffer={counterOffer} 
                  user={user}
                  onUpdated={onOfferUpdated}
                  onError={(msg) => setMessage({ type: msg.startsWith('✅') ? 'success' : 'error', text: msg })}
                />
              ))}
              {offers.length > 0 && <h3 className="text-xl font-bold text-[#1e293b] mt-8 mb-4">{t('profile.tabs.offers')}</h3>}
            </>
          )}
          {offers.map((offer) => (
            <div key={offer.id} className="football-card glass-football p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-xl font-bold text-[#1e293b] mb-2">
                    {t('trade.offerFrom', { username: offer.user1Username })}
                  </h3>
                  <p className="text-sm text-gray-400">
                    {t('trade.expires', { date: new Date(offer.expiresAt).toLocaleDateString(locale || 'it-IT') })}
                  </p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div className="p-4 bg-blue-500/10 border-2 border-blue-500/30 rounded-lg">
                  <p className="text-sm text-gray-400 mb-1">{t('trade.theyOffer')}:</p>
                  <p className="font-semibold text-white">{offer.card1?.playerName || t('trade.unknownPlayer')}</p>
                  <p className="text-xs text-gray-400">{offer.card1?.rarity || t('trade.unknownRarity')}</p>
                </div>
                <div className="p-4 bg-green-500/10 border-2 border-green-500/30 rounded-lg">
                  <p className="text-sm text-gray-400 mb-1">{t('trade.youGive')}:</p>
                  <p className="font-semibold text-white">{offer.card2?.playerName || t('trade.unknownPlayer')}</p>
                  <p className="text-xs text-gray-400">{offer.card2?.rarity || t('trade.unknownRarity')}</p>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => handleAcceptTrade(offer.id)}
                  disabled={processing}
                  className="btn-football flex-1 bg-green-600 hover:bg-green-700"
                >
                  {processing ? t('marketplaceCards.processing') : t('trade.accept')}
                </button>
                <button
                  onClick={() => handleOpenCounterOffer(offer)}
                  disabled={processing || loadingCards}
                  className="px-4 py-3 bg-yellow-600 hover:bg-yellow-700 text-white font-semibold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loadingCards ? t('common.loading') : t('trade.counterOffer')}
                </button>
                <button
                  onClick={() => handleRejectTrade(offer.id)}
                  disabled={processing}
                  className="px-6 py-3 border-2 border-red-500 text-red-300 hover:bg-red-500/20 font-semibold rounded-lg transition-all"
                >
                  {t('trade.reject')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Counter Offer Modal */}
      {showCounterOfferModal && selectedOffer && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="football-card glass-football p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold text-[#1e293b] mb-4">{t('trade.counterOfferTitle')}</h2>
            <div className="mb-4">
              <p className="text-gray-300 mb-2">
                {t('trade.theyWant')}: <strong>{selectedOffer.card2?.playerName || t('trade.yourCard')}</strong>
              </p>
              <p className="text-gray-300 mb-4">
                {t('trade.youWant')}: <strong>{selectedOffer.card1?.playerName || t('trade.theirCard')}</strong>
              </p>
              
              <label className="block text-gray-300 mb-2">{t('trade.selectCardInstead')}</label>
              <div className="max-h-60 overflow-y-auto space-y-2">
                {userCards.map((card) => (
                  <div
                    key={card.id}
                    onClick={() => setSelectedCounterCardId(card.id)}
                    className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      selectedCounterCardId === card.id
                        ? 'border-yellow-500 bg-yellow-500/20'
                        : 'border-gray-600 hover:border-gray-500 bg-gray-800/50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-white">{card.player?.full_name || t('common.unknown')}</p>
                        <p className="text-xs text-gray-400">
                          {card.rarity?.name || t('common.unknown')} • {card.player?.position || t('common.notAvailable')}
                        </p>
                      </div>
                      {selectedCounterCardId === card.id && (
                        <div className="text-yellow-500">✓</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex space-x-4">
              <button
                onClick={handleCreateCounterOffer}
                disabled={processing || !selectedCounterCardId}
                className="btn-football flex-1 bg-yellow-600 hover:bg-yellow-700"
              >
                {processing ? t('trade.creating') : t('trade.sendCounter')}
              </button>
              <button
                onClick={() => {
                  setShowCounterOfferModal(false);
                  setSelectedOffer(null);
                  setSelectedCounterCardId(null);
                }}
                className="px-6 py-3 border-2 border-gray-500 text-gray-300 hover:bg-gray-700 font-semibold rounded-lg transition-all"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OffersTab;
